import ReactDOM from 'react-dom';
import { CheckCircle, Lock, Unlock, Check, Edit2, Trash2 } from 'react-feather';

// The popover rendered via createPortal when a group's kebab menu is open.
// Pure presentation: all interactions surface as callback props so the
// owning selector keeps the state machine in one place.
//
// Props:
//   group              — the group object (must have id, name, linked, fundNumbers)
//   kebabPos           — { top, left } from the trigger button's bounding rect
//   confirmDeleteId    — when === group.id, show the delete-confirm row
//   setConfirmDeleteId — setter (null to dismiss, group.id to arm)
//   confirmModeChange  — when .groupId === group.id, show the mode-change confirm row
//   setConfirmModeChange — setter
//   onMoveToReady      — () => void
//   onRename           — () => void  (caller arms rename inline-edit state)
//   onDeleteConfirmed  — () => void
//   onToggleLinkedConfirmed — (newLinked: boolean) => void
//   onClose            — () => void (backdrop click)
function GroupKebabPopover({
    group, kebabPos,
    confirmDeleteId, setConfirmDeleteId,
    confirmModeChange, setConfirmModeChange,
    onMoveToReady, onRename, onDeleteConfirmed, onToggleLinkedConfirmed, onClose,
}) {
    if (!group || !kebabPos) return null;
    return ReactDOM.createPortal(
        <>
            <div
                className="sg-kebab-backdrop"
                onClick={onClose}
                style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'transparent' }}
            />
            <div
                className="sg-popover"
                style={{ position: 'fixed', top: kebabPos.top, left: kebabPos.left, right: 'auto', width: 260, zIndex: 9999 }}
            >
                <button className="sg-menu-item sg-menu-item--primary" onClick={onMoveToReady}>
                    <CheckCircle size={14} className="sg-menu-icon" style={{ color: '#27C281' }} />
                    <span className="sg-menu-label">Move to Ready to Publish</span>
                </button>
                <div className="sg-divider" />
                <span className="sg-mode-section-label">Editing mode</span>
                {confirmModeChange?.groupId === group.id ? (
                    <div className="sg-delete-confirm">
                        <span className="sg-delete-confirm-label">
                            {confirmModeChange.newLinked
                                ? 'Switch to Linked? Edits will apply to all members — individual changes may be overwritten.'
                                : 'Switch to Individual? Members will no longer share edits automatically.'}
                        </span>
                        <div className="sg-delete-confirm-actions">
                            <button className="sg-delete-confirm-btn sg-delete-confirm-btn--cancel" onClick={() => setConfirmModeChange(null)}>Cancel</button>
                            <button
                                className="sg-delete-confirm-btn sg-delete-confirm-btn--primary"
                                onClick={() => onToggleLinkedConfirmed(confirmModeChange.newLinked)}
                            >Switch</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <button
                            className={`sg-mode-card${group.linked !== false ? ' sg-mode-card--active' : ''}`}
                            onClick={() => { if (group.linked === false) setConfirmModeChange({ groupId: group.id, newLinked: true }); }}
                        >
                            <div className={`sg-mode-icon-tile${group.linked !== false ? ' sg-mode-icon-tile--active' : ''}`}><Lock size={13} /></div>
                            <div className="sg-mode-card-text">
                                <span className="sg-mode-title">Linked edits</span>
                                <span className="sg-mode-desc">All members get the same edits</span>
                            </div>
                            {group.linked !== false && <Check size={13} className="sg-mode-check" />}
                        </button>
                        <button
                            className={`sg-mode-card${group.linked === false ? ' sg-mode-card--active' : ''}`}
                            onClick={() => { if (group.linked !== false) setConfirmModeChange({ groupId: group.id, newLinked: false }); }}
                        >
                            <div className={`sg-mode-icon-tile${group.linked === false ? ' sg-mode-icon-tile--active' : ''}`}><Unlock size={13} /></div>
                            <div className="sg-mode-card-text">
                                <span className="sg-mode-title">Individual edits</span>
                                <span className="sg-mode-desc">Edit each portfolio on its own</span>
                            </div>
                            {group.linked === false && <Check size={13} className="sg-mode-check" />}
                        </button>
                    </>
                )}
                <div className="sg-divider" />
                <button className="sg-menu-item sg-menu-item--neutral" onClick={onRename}>
                    <Edit2 size={14} className="sg-menu-icon" />
                    <span className="sg-menu-label">Rename group</span>
                </button>
                {confirmDeleteId === group.id ? (
                    <div className="sg-delete-confirm">
                        <span className="sg-delete-confirm-label">Delete this group?</span>
                        <div className="sg-delete-confirm-actions">
                            <button className="sg-delete-confirm-btn sg-delete-confirm-btn--cancel" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                            <button className="sg-delete-confirm-btn sg-delete-confirm-btn--confirm" onClick={onDeleteConfirmed}>Delete</button>
                        </div>
                    </div>
                ) : (
                    <button className="sg-menu-item sg-menu-item--danger" onClick={() => setConfirmDeleteId(group.id)}>
                        <Trash2 size={14} className="sg-menu-icon" />
                        <span className="sg-menu-label">Delete group</span>
                    </button>
                )}
            </div>
        </>,
        document.body,
    );
}

export default GroupKebabPopover;
